import React, { useCallback, useMemo } from 'react';

// DEPENDENCIES
import snakeCase from 'lodash/snakeCase';
import { useParams } from 'react-router';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';

// FUNCTIONS
import { documentFetch, fileDownload, makePath } from 'functions.js';

// GLOBAL COMPONENTS
import Button from 'core/tools/Button';
import Dropdown from 'core/tools/dropdown/Dropdown';
import Sprite from 'core/tools/Sprite';
import Item from 'routers/interface/tools/Item';

const OPTIONS = [
  { label: "Buyer's Order", value: 'buyersOrder', endpoint: ENDPOINTS.sales.print.buyersOrder },
  { label: 'Trade Payoff' },
  { label: 'Gross Recap', value: 'grossRecap', endpoint: ENDPOINTS.sales.print.recap, params: { recapType: 'Gross' } },
  { label: 'Fallout Recap', value: 'falloutRecap', endpoint: ENDPOINTS.sales.print.recap, params: { recapType: 'Fallout' } },
  { label: 'Backout Recap', value: 'backoutRecap', endpoint: ENDPOINTS.sales.print.recap, params: { recapType: 'Backout' } },
  { label: 'Clean Up Form', value: 'cleanUpForm', endpoint: ENDPOINTS.sales.print.cleanUp  },
  { label: 'TWG Cancellation', value: 'twgCancellation', endpoint: ENDPOINTS.sales.print.twg }
];

// MAIN COMPONENT
const PrintSelectButton = ({
  customerName, //
  dailySalesInventoryId,
  isBackout,
  isFallout,
  salesId,
  stockNum
}) => {
  const { recordID } = useParams();

  const options = useMemo(() => {
    const filtered = OPTIONS.filter(o => {
      if (o.value === 'falloutRecap' && !isFallout) return false;
      if (o.value === 'backoutRecap' && !isBackout) return false;
      return true;
    });
    return filtered.map(o => ({
      ...o,
      endpoint: o.value === 'cleanUpForm' ? o.endpoint(salesId, dailySalesInventoryId) : makePath(o.endpoint, recordID)
    }));
  }, [isBackout, isFallout, salesId, dailySalesInventoryId, recordID]);

  const handlePrint = useCallback(
    ({ label, endpoint, params }) => {
      const args = {
        endpoint,
        accept: 'pdf',
        contentType: 'pdf',
        mimeType: 'application/pdf',
        params,
        loadingMessage: 'Downloading report',
        errorMessage: 'Unable to download report.',
        successMessage: 'Report downloaded.',
        onSuccess: ({ url }) => {
          return fileDownload(url, `${stockNum}_${snakeCase(customerName)}_${snakeCase(label)}`, undefined, true, true);
        }
      };
      documentFetch(args);
    },
    [customerName, stockNum]
  );

  // RENDER
  return (
    <Dropdown
      toggle={{
        as: Button,
        icon: 'chevron-down',
        size: 'sm',
        fullWidth: true,
        outline: true,
        children: (
          <>
            <Sprite use="print" />
            <span className="ms-2">Print</span>
          </>
        )
      }}
      size="sm"
    >
      {options.map((o, i) => (
        <Item option={o.label} key={i} onSelect={() => handlePrint(o)} disabled={!o.value || !o.endpoint} />
      ))}
    </Dropdown>
  );
};

// EXPORT
export default PrintSelectButton;
